import type { MessageDescriptor } from 'react-intl';

import { CODE_ERRORS } from 'constants/Global';
import type { ApiError, ApiFailureAction } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';

type ConflictNotification = {
  matches: (errorMessage: string) => boolean;
  notification: MessageDescriptor;
};

const CONFLICT_NOTIFICATIONS: ConflictNotification[] = [
  {
    matches: errorMessage => errorMessage.startsWith('This product token is already used'),
    notification: {
      id: 'page.config.smsAccount.error.duplicateToken',
      defaultMessage: 'This product token is already used by another account.',
    },
  },
  {
    matches: errorMessage => errorMessage.startsWith('Messaging account name'),
    notification: {
      id: 'page.config.smsAccount.error.duplicateName',
      defaultMessage: 'An account with this name already exists.',
    },
  },
  {
    matches: errorMessage => errorMessage.endsWith('is still used by a dealer'),
    notification: {
      id: 'page.smsAccounts.dashboard.delete.inUse',
      defaultMessage: "This account cannot be deleted as it's used by a dealer.",
    },
  },
];

/**
 * Builds a failure notification that names the conflict the API reported in a 409, and falls back
 * to `fallback` for any other failure. The API sends no machine code, only a message to match on.
 */
export const conflictFailureNotification =
  (fallback: MessageDescriptor) =>
    ({ payload }: ApiFailureAction): MessageDescriptor => {
      if (payload.response?.status !== CODE_ERRORS.CONFLICT_ERROR) {
        return fallback;
      }
      const errorMessage = payload.response.data?.errorMessage ?? '';
      return CONFLICT_NOTIFICATIONS.find(({ matches }) => matches(errorMessage))?.notification ?? fallback;
    };

const REJECTED_SMS_PHONE_NUMBER = /^Phone number \[(.+?)\] is not an SMS phone number of messaging account/;

/** The SMS phone number a dealer save was refused for, when its messaging account does not hold it. */
export const getRejectedSmsPhoneNumber = (error: ApiError | null): MayBe<string> => {
  if (error?.response?.status !== CODE_ERRORS.BAD_REQUEST) {
    return undefined;
  }
  return REJECTED_SMS_PHONE_NUMBER.exec(error.response.data?.errorMessage ?? '')?.[1];
};
